import React, { FC, ReactNode } from "react";
import classNames from "classnames/dedupe";
import { ILink, Link } from "../../ActionsSelectors/Link";
import { TThemeVariant, useTheme } from "../../Utility/ThemeProvider";
import { THeading } from "../../../interfaces";

export interface IProductTitleProps {
  /**
   * Variant of layout for correspondent pages
   */
  variant?: "PLP" | "PDP";

  /**
   * Link object to help navigate to the brand page
   */
  brandLink?: ILink;

  /**
   * Link object to help navigate to the product
   */
  productLink?: ILink;

  /**
   * Product brand string
   */
  brandName?: ReactNode;

  /**
   * Amount of lines that will be visible for brand's name
   */
  brandMaxLines?: number;

  /**
   * Product model string
   */
  productName?: ReactNode;

  /**
   * Amount of lines that will be visible for product's name
   */
  productMaxLines?: number;

  /**
   * Size default or small (shop the look)
   */
  size?: "small" | "default";

  /** Current theme */
  theme?: TThemeVariant;

  /** Heading tag */
  useHeading?: THeading;
}

const ProductTitle: FC<IProductTitleProps> = ({
  variant = "PLP",
  size,
  brandLink,
  productLink,
  brandMaxLines = 2,
  brandName,
  productName,
  productMaxLines = 2,
  theme: propsTheme,
  useHeading,
}) => {
  const theme = useTheme(propsTheme);

  const Component = useHeading || React.Fragment;

  return variant === "PLP" ? (
    <div
      className={classNames("product-title", {
        [`max-lines max-lines--${brandMaxLines}`]: brandMaxLines,
        "product-title--size-small": size === "small",
        "text-color-white": theme === "dark",
        "text-color-dark-blue": theme === "light",
      })}
      data-testid="Product_Title_PLP_variant"
    >
      <Component>
        <Link
          classes={{
            root: classNames("product-brand text-decoration-none", {
              "cursor-pointer": brandLink?.href,
              "text-color-white": theme === "dark",
              "text-color-pressed-white-hover":
                theme === "dark" && brandLink?.href,
            }),
          }}
          {...brandLink}
        >
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <>{brandName}</>
        </Link>
        <Link
          classes={{
            root: classNames("product-model text-decoration-none", {
              "cursor-pointer": productLink?.href,
              "text-color-white": theme === "dark",
              "text-color-pressed-white-hover":
                theme === "dark" && productLink?.href,
              [`max-lines max-lines--${productMaxLines}`]: productMaxLines,
            }),
          }}
          {...productLink}
        >
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <>{productName}</>
        </Link>
      </Component>
    </div>
  ) : (
    <div className="product-title pdp-main">
      <Component>
        <Link
          classes={{
            root: classNames("product-brand cursor-pointer", {
              "text-color-white text-color-pressed-white-hover":
                theme === "dark",
              [`max-lines max-lines--${brandMaxLines}`]: brandMaxLines,
            }),
          }}
          {...brandLink}
        >
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <>{brandName}</>
        </Link>
        <div className="product-model">
          <Link
            classes={{
              root: classNames("cursor-pointer", {
                "text-color-white text-color-pressed-white-hover":
                  theme === "dark",
                [`max-lines max-lines--${productMaxLines}`]: productMaxLines,
              }),
            }}
            {...productLink}
          >
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            <>{productName}</>
          </Link>
        </div>
      </Component>
    </div>
  );
};

export default ProductTitle;
